/**
 * AdminInventory — cross-dealer inventory command console.
 *
 * Route: `/admin/inventory`
 *
 * Capabilities (surface all admin superpowers Phase 10 requires):
 *   • Table across every dealer's inventory — filterable by dealer / status
 *     / sync_source, keyword search over VIN/stock/make/model.
 *   • Row actions:
 *       - Mark sold (with optional sale price override)
 *       - Transfer to another dealer (Move dialog)
 *       - Soft-delete
 *   • Stock number column is a first-class citizen so ops staff can look up
 *     a car from a paper invoice quickly.
 *
 * Uses:
 *   GET/POST/PATCH/DELETE /api/admin/inventory
 *   POST /api/admin/inventory/{id}/mark-sold
 */
import React, { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import {
  ChevronLeft, ChevronRight, DollarSign, ExternalLink, FileEdit, Repeat, Search, Trash2, X,
} from "lucide-react";
import { api, formatApiError } from "@/lib/api";
import { AdminPage } from "@/pages/admin/AdminLayout";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import {
  Dialog, DialogClose, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle,
} from "@/components/ui/dialog";
import { formatMiles, formatUSD, titleCase } from "@/lib/format";

const ANY = "__any__";
const PAGE_SIZE = 25;

const STATUS_COLOR = {
  available: "bg-emerald-50 text-emerald-700 border-emerald-200",
  pending:   "bg-amber-50 text-amber-700 border-amber-200",
  sold:      "bg-rose-50 text-rose-700 border-rose-200",
};

const AdminInventory = () => {
  const [dealers, setDealers] = useState([]);
  const [q, setQ] = useState("");
  const [dealerId, setDealerId] = useState(ANY);
  const [statusFilter, setStatusFilter] = useState(ANY);
  const [syncSource, setSyncSource] = useState(ANY);
  const [page, setPage] = useState(1);
  const [data, setData] = useState({ items: [], total: 0 });
  const [loading, setLoading] = useState(true);

  const [soldTarget, setSoldTarget] = useState(null);   // {vehicle, sale_price}
  const [moveTarget, setMoveTarget] = useState(null);   // {vehicle, dest_dealer_id}
  const [deleteTarget, setDeleteTarget] = useState(null);

  useEffect(() => {
    api.get("/admin/dealers").then((r) => setDealers(r.data || [])).catch(() => setDealers([]));
  }, []);

  const params = useMemo(() => {
    const p = { page, page_size: PAGE_SIZE };
    if (q) p.q = q;
    if (dealerId !== ANY) p.dealer_id = dealerId;
    if (statusFilter !== ANY) p.status = statusFilter;
    if (syncSource !== ANY) p.sync_source = syncSource;
    return p;
  }, [q, dealerId, statusFilter, syncSource, page]);

  const reload = () => {
    setLoading(true);
    api.get("/admin/inventory", { params })
      .then((r) => setData(r.data))
      .catch(() => setData({ items: [], total: 0 }))
      .finally(() => setLoading(false));
  };

  useEffect(() => { reload(); }, [params]); // eslint-disable-line react-hooks/exhaustive-deps

  const totalPages = Math.max(1, Math.ceil(data.total / PAGE_SIZE));
  const dealerName = (id) => dealers.find((d) => d.id === id)?.name || "—";

  const confirmMarkSold = async () => {
    if (!soldTarget?.vehicle) return;
    try {
      await api.post(`/admin/inventory/${soldTarget.vehicle.id}/mark-sold`, {
        sale_price: soldTarget.sale_price ? Number(soldTarget.sale_price) : null,
      });
      toast.success("Vehicle marked as sold.");
      setSoldTarget(null);
      reload();
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const confirmMove = async () => {
    if (!moveTarget?.vehicle || !moveTarget.dest_dealer_id) return;
    try {
      await api.patch(`/admin/inventory/${moveTarget.vehicle.id}`, {
        dealer_id: moveTarget.dest_dealer_id,
      });
      toast.success("Vehicle transferred.");
      setMoveTarget(null);
      reload();
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const confirmDelete = async () => {
    if (!deleteTarget) return;
    try {
      await api.delete(`/admin/inventory/${deleteTarget.id}`);
      toast.success("Vehicle removed.");
      setDeleteTarget(null);
      reload();
    } catch (e) { toast.error(formatApiError(e)); }
  };

  return (
    <AdminPage
      title="Inventory"
      subtitle={`Cross-dealer marketplace view — ${data.total.toLocaleString()} vehicles across ${dealers.length} dealers.`}
      testId="admin-inventory-page"
      action={
        <Button variant="outline" onClick={() => api.post("/admin/seo/regenerate-sitemap").then(() => toast.success("Sitemap regenerated")).catch((e) => toast.error(formatApiError(e)))} data-testid="admin-regen-sitemap-btn">
          Regenerate sitemap.xml
        </Button>
      }
    >
      {/* Filters row */}
      <div className="grid grid-cols-1 sm:grid-cols-[1fr_180px_140px_160px] gap-2 mb-4">
        <div className="relative">
          <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
          <Input
            value={q}
            onChange={(e) => { setQ(e.target.value); setPage(1); }}
            placeholder="Search VIN, stock, make, model…"
            className="pl-9"
            data-testid="admin-inv-search"
          />
        </div>
        <Select value={dealerId} onValueChange={(v) => { setDealerId(v); setPage(1); }}>
          <SelectTrigger data-testid="admin-inv-dealer-select"><SelectValue placeholder="Any dealer" /></SelectTrigger>
          <SelectContent>
            <SelectItem value={ANY}>Any dealer</SelectItem>
            {dealers.map((d) => <SelectItem key={d.id} value={d.id}>{d.name}</SelectItem>)}
          </SelectContent>
        </Select>
        <Select value={statusFilter} onValueChange={(v) => { setStatusFilter(v); setPage(1); }}>
          <SelectTrigger data-testid="admin-inv-status-select"><SelectValue placeholder="Any status" /></SelectTrigger>
          <SelectContent>
            <SelectItem value={ANY}>Any status</SelectItem>
            <SelectItem value="available">Available</SelectItem>
            <SelectItem value="pending">Pending</SelectItem>
            <SelectItem value="sold">Sold</SelectItem>
          </SelectContent>
        </Select>
        <Select value={syncSource} onValueChange={(v) => { setSyncSource(v); setPage(1); }}>
          <SelectTrigger data-testid="admin-inv-sync-select"><SelectValue placeholder="Any source" /></SelectTrigger>
          <SelectContent>
            <SelectItem value={ANY}>Any source</SelectItem>
            <SelectItem value="vinsolutions">VinSolutions</SelectItem>
            <SelectItem value="dealercenter">DealerCenter</SelectItem>
            <SelectItem value="vauto">vAuto</SelectItem>
            <SelectItem value="manual">Manual</SelectItem>
            <SelectItem value="manual_csv">CSV import</SelectItem>
          </SelectContent>
        </Select>
      </div>

      {/* Table */}
      <div className="bg-white border border-slate-200 rounded-2xl overflow-hidden">
        {loading ? (
          <div className="h-96 animate-pulse bg-slate-100" />
        ) : data.items.length === 0 ? (
          <div className="p-10 text-center text-slate-500" data-testid="admin-inv-empty">
            No vehicles match those filters.
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm" data-testid="admin-inv-table">
              <thead className="text-[10px] uppercase tracking-widest text-slate-500 bg-slate-50">
                <tr>
                  <th className="text-left p-3">Stock</th>
                  <th className="text-left p-3">Vehicle</th>
                  <th className="text-left p-3">Dealer</th>
                  <th className="text-right p-3">Price</th>
                  <th className="text-right p-3">Miles</th>
                  <th className="text-left p-3">Status</th>
                  <th className="text-left p-3">Source</th>
                  <th className="text-left p-3">Listed</th>
                  <th className="text-right p-3">Actions</th>
                </tr>
              </thead>
              <tbody>
                {data.items.map((v) => (
                  <tr key={v.id} className="border-t border-slate-100 hover:bg-slate-50" data-testid={`admin-inv-row-${v.id}`}>
                    <td className="p-3 font-mono text-xs font-bold text-slate-900">{v.stock_number || "—"}</td>
                    <td className="p-3">
                      <Link to={`/vehicle/${v.id}`} target="_blank" className="font-medium text-slate-900 hover:text-gold inline-flex items-center gap-1" data-testid={`admin-inv-link-${v.id}`}>
                        {v.year} {v.make} {v.model}
                        <ExternalLink size={11} className="opacity-60" />
                      </Link>
                      <p className="text-[10px] font-mono text-slate-400 mt-0.5">{v.vin}</p>
                    </td>
                    <td className="p-3">
                      <span className="text-slate-700">{v.dealer?.name || dealerName(v.dealer_id)}</span>
                      <p className="text-[10px] text-slate-400">{v.dealer?.city}</p>
                    </td>
                    <td className="p-3 text-right font-mono text-slate-900">{formatUSD(v.price)}</td>
                    <td className="p-3 text-right font-mono text-slate-600 text-xs">{formatMiles(v.mileage)}</td>
                    <td className="p-3">
                      <span className={`inline-block text-[10px] font-bold uppercase tracking-wider px-2 py-0.5 rounded-full border ${STATUS_COLOR[v.status] || "bg-slate-100 text-slate-700"}`}>
                        {v.status}
                      </span>
                    </td>
                    <td className="p-3 text-[11px] text-slate-500 uppercase tracking-wider">{v.sync_source}</td>
                    <td className="p-3 text-[10px] text-slate-500">{v.listed_at?.slice(0, 10)}</td>
                    <td className="p-3 text-right">
                      <div className="inline-flex gap-1">
                        {v.status !== "sold" && (
                          <Button
                            variant="ghost"
                            size="sm"
                            className="h-7 px-2 text-emerald-700 hover:bg-emerald-50"
                            onClick={() => setSoldTarget({ vehicle: v, sale_price: v.price })}
                            data-testid={`admin-inv-mark-sold-${v.id}`}
                          >
                            <DollarSign size={12} /> Mark sold
                          </Button>
                        )}
                        <Button
                          variant="ghost"
                          size="sm"
                          className="h-7 px-2 text-slate-700 hover:bg-slate-100"
                          onClick={() => setMoveTarget({ vehicle: v, dest_dealer_id: v.dealer_id })}
                          data-testid={`admin-inv-move-${v.id}`}
                          title="Transfer to another dealer"
                        >
                          <Repeat size={12} />
                        </Button>
                        <Button
                          variant="ghost"
                          size="sm"
                          className="h-7 px-2 text-rose-700 hover:bg-rose-50"
                          onClick={() => setDeleteTarget(v)}
                          data-testid={`admin-inv-delete-${v.id}`}
                          title="Delete listing"
                        >
                          <Trash2 size={12} />
                        </Button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Pagination */}
      {totalPages > 1 && (
        <div className="mt-4 flex items-center justify-between">
          <p className="text-xs text-slate-500">Page {page} of {totalPages}</p>
          <div className="flex items-center gap-2">
            <Button variant="outline" disabled={page <= 1} onClick={() => setPage((p) => Math.max(1, p - 1))} data-testid="admin-inv-prev">
              <ChevronLeft size={14} /> Prev
            </Button>
            <Button variant="outline" disabled={page >= totalPages} onClick={() => setPage((p) => Math.min(totalPages, p + 1))} data-testid="admin-inv-next">
              Next <ChevronRight size={14} />
            </Button>
          </div>
        </div>
      )}

      {/* Mark sold dialog */}
      <Dialog open={!!soldTarget} onOpenChange={() => setSoldTarget(null)}>
        <DialogContent data-testid="admin-inv-sold-dialog">
          <DialogHeader>
            <DialogTitle>Mark as sold</DialogTitle>
            <DialogDescription>
              {soldTarget?.vehicle.year} {soldTarget?.vehicle.make} {soldTarget?.vehicle.model} · stock {soldTarget?.vehicle.stock_number || "—"}
            </DialogDescription>
          </DialogHeader>
          <div>
            <Label>Sale price (optional)</Label>
            <Input
              type="number" min={0} step={100}
              value={soldTarget?.sale_price || ""}
              onChange={(e) => setSoldTarget((t) => ({ ...t, sale_price: e.target.value }))}
              placeholder={String(soldTarget?.vehicle.price || "0")}
              data-testid="admin-inv-sold-price"
            />
            <p className="text-[11px] text-slate-500 mt-1">Leave blank to use list price.</p>
          </div>
          <DialogFooter>
            <DialogClose asChild><Button variant="outline">Cancel</Button></DialogClose>
            <Button className="bg-emerald-600 hover:bg-emerald-500 text-white" onClick={confirmMarkSold} data-testid="admin-inv-sold-confirm">
              Mark sold
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Move dialog */}
      <Dialog open={!!moveTarget} onOpenChange={() => setMoveTarget(null)}>
        <DialogContent data-testid="admin-inv-move-dialog">
          <DialogHeader>
            <DialogTitle>Transfer vehicle</DialogTitle>
            <DialogDescription>
              Move {moveTarget?.vehicle.year} {moveTarget?.vehicle.make} {moveTarget?.vehicle.model} to a different dealership.
            </DialogDescription>
          </DialogHeader>
          <div>
            <Label>Destination dealer</Label>
            <Select value={moveTarget?.dest_dealer_id} onValueChange={(v) => setMoveTarget((t) => ({ ...t, dest_dealer_id: v }))}>
              <SelectTrigger data-testid="admin-inv-move-dealer-select"><SelectValue /></SelectTrigger>
              <SelectContent>
                {dealers.map((d) => (
                  <SelectItem key={d.id} value={d.id}>
                    {d.name} — {d.city}
                    {d.id === moveTarget?.vehicle?.dealer_id && " (current)"}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <DialogFooter>
            <DialogClose asChild><Button variant="outline">Cancel</Button></DialogClose>
            <Button
              className="bg-slate-950 hover:bg-slate-800 text-white"
              onClick={confirmMove}
              disabled={!moveTarget || moveTarget.dest_dealer_id === moveTarget.vehicle?.dealer_id}
              data-testid="admin-inv-move-confirm"
            >
              Transfer
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Delete dialog */}
      <Dialog open={!!deleteTarget} onOpenChange={() => setDeleteTarget(null)}>
        <DialogContent data-testid="admin-inv-delete-dialog">
          <DialogHeader>
            <DialogTitle>Delete listing?</DialogTitle>
            <DialogDescription>
              This soft-deletes the vehicle — it stops appearing in inventory but the row remains for audit.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <DialogClose asChild><Button variant="outline">Cancel</Button></DialogClose>
            <Button className="bg-rose-600 hover:bg-rose-700 text-white" onClick={confirmDelete} data-testid="admin-inv-delete-confirm">
              Delete listing
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </AdminPage>
  );
};

export default AdminInventory;
